import React from 'react';
import { Button } from 'src/components/ui/button';
import { Account, Campaign } from '../types/client-management.types';
import SearchInput from 'src/components/search-input/search-input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

export interface ManageToolbarOrderingOption {
  value: string;
  label: string;
}

interface AccountCampaignMenubarProps {
  selectedFilter?: 'attention_needed' | 'all';
  searchQuery: string;
  filterSelect?: (filter: 'attention_needed' | 'all') => void;
  searchChange: (search: string) => void;
  className?: string;
  items: Account[] | Campaign[];
  label?: string;
  searchPlaceholder?: string;
  /** Omit for APIs that do not support sort (e.g. GET accounts). */
  orderingOptions?: ManageToolbarOrderingOption[];
  orderingValue?: string;
  onOrderingChange?: (value: string) => void;
}

const AccountCampaignMenubar: React.FC<AccountCampaignMenubarProps> = ({
  selectedFilter,
  filterSelect,
  searchQuery,
  searchChange,
  className,
  items,
  label,
  searchPlaceholder = 'Search…',
  orderingOptions,
  orderingValue = '',
  onOrderingChange,
}) => {
  // @ts-expect-error - items is an array of Account or Campaign
  const needsAttentionCount = items && items.length > 0 && items.filter((item: Account | Campaign) => item.attention_needed).length;

  const sortLabel =
    orderingOptions?.find((o) => o.value === orderingValue)?.label ?? 'Sort';

  return (
    <div className="flex flex-nowrap items-center justify-between w-full gap-3 min-w-0">
      <div className="flex flex-nowrap items-center gap-2 min-w-0 flex-1">
        <SearchInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={searchChange}
          debounceMs={500}
          className="min-w-[10rem] flex-1 max-w-md shrink"
          iconClassName="text-gray-600"
          inputClassName="bg-gray-201 rounded-full placeholder:text-gray-600"
        />
        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={`${className} hover:opacity-65 gap-1.5 px-4`}
                variant="ghost"
                size="med"
              >
                <ChevronDown className="h-4 w-4 shrink-0" />
                <span className="max-w-[11rem] truncate text-sm font-normal">
                  {selectedFilter === 'attention_needed' ? 'Attention needed' : 'All'}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => filterSelect?.('attention_needed')}>
                Attention needed
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => filterSelect?.('all')}>
                All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {orderingOptions && orderingOptions.length > 0 && onOrderingChange ? (
          <div className="shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className={`${className} hover:opacity-65 gap-1.5 px-4`}
                  variant="ghost"
                  size="med"
                >
                  <ChevronDown className="h-4 w-4 shrink-0" />
                  <span className="max-w-[11rem] truncate text-sm font-normal">
                    {sortLabel}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="max-h-72 overflow-y-auto"
              >
                {orderingOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => onOrderingChange(opt.value)}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
      {label ? (
        <div className="flex shrink-0 items-center gap-2">
          <div className="relative">
            <span className="text-white bg-red-500 rounded-full px-1 items-center text-[10px] font-thin absolute top-0 right-0">
              {Number(needsAttentionCount)}
            </span>
            <span className="text-gray-700 px-2 py-2 text-2xl font-thin whitespace-nowrap">
              {label}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountCampaignMenubar;
